/**
 * Guards an optimistically-updated boolean toggle against stale MQTT echoes.
 *
 * The lighting toggles reflect the resident's choice immediately (optimistic),
 * then the real device state streams back over MQTT. Because that stream
 * re-emits the whole device-state map on every unrelated update (see
 * MqttMessageStorageService / getMessageListener), a not-yet-caught-up "old"
 * value would otherwise overwrite the optimistic one and make the toggle
 * visibly bounce (on → off → on).
 *
 * While a change is pending this gate:
 *   - ignores echoes that don't match the requested value (the stale old state),
 *   - clears itself the moment the confirming echo arrives, then resumes
 *     tracking real device state, and
 *   - falls back to the previous value if neither a confirmation nor a publish
 *     failure arrives within `timeoutMs`.
 *
 * Each `begin()` returns a token identifying that command. Reverts (timeout or
 * publish failure) only fire for the command that is *still* the pending one —
 * a stale failure from a superseded or already-confirmed command is a no-op, so
 * it can't clobber a newer value or tear down the in-flight command's tracking.
 *
 * This mirrors the optimistic-update + revert pattern already used by the
 * dashboard control widgets (arm-slider, light-control, climate-control).
 */
export class PendingToggleGate {
  private pending = false;
  private target = false;
  private previous = false;
  private token = 0;
  private revert: (previous: boolean) => void = () => {};
  private timer: ReturnType<typeof setTimeout> | null = null;

  /**
   * @param timeoutMs how long to hold the optimistic value while awaiting a
   *   confirming echo before reverting.
   */
  constructor(private readonly timeoutMs: number) {}

  get isPending(): boolean {
    return this.pending;
  }

  /**
   * Record a user-initiated change and return a token identifying it. `revert`
   * runs (with `previous`) if this exact change neither confirms nor is
   * explicitly failed before the timeout elapses.
   */
  begin(target: boolean, previous: boolean, revert: (previous: boolean) => void): number {
    this.pending = true;
    this.target = target;
    this.previous = previous;
    this.revert = revert;
    const token = ++this.token;
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.failIfCurrent(token);
    }, this.timeoutMs);
    return token;
  }

  /**
   * Decide whether an incoming reported value should update the UI. Applies
   * (and confirms) a value matching the pending target; ignores the stale
   * opposite echo while pending. When not pending, everything is applied so
   * externally-driven changes are still honoured.
   */
  shouldApply(reported: boolean): boolean {
    if (!this.pending) {
      return true;
    }
    if (reported === this.target) {
      this.settle();
      return true;
    }
    return false;
  }

  /**
   * Revert a command (publish failed) — but only if it is still the pending
   * one. A stale failure from a superseded or already-confirmed command is
   * ignored, leaving the current value untouched.
   */
  failIfCurrent(token: number): void {
    if (this.pending && token === this.token) {
      const revert = this.revert;
      const previous = this.previous;
      this.settle();
      revert(previous);
    }
  }

  /** Abandon any pending change without reverting (e.g. on teardown). */
  cancel(): void {
    this.settle();
  }

  private settle(): void {
    this.pending = false;
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
